/**
 * 네트 부팅 - 서버 주소 · 재접속 토큰의 `sessionStorage` · 소켓 전송 만들기.
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 7.3절(정적은 nginx, 소켓은 `/ws`) · `이상한방-상태모델-v0.1.md` 11.3절
 *            로드맵 M6(`netBoot.ts` - `VITE_WS_URL`, 없으면 같은 호스트 `/ws`. **`sessionStorage`를 아는 파일은 이것 하나다**)
 *
 * ## 서버 주소
 *
 * 개발에서는 Vite가 `/ws`를 3005로 프록시하고(`vite.config.ts`), 배포에서는 nginx가 같은 경로를 넘긴다(`deploy/nginx/wierd-room.conf`).
 * 그래서 기본값은 "지금 페이지와 같은 호스트의 `/ws`"이고 프로토콜만 페이지를 따른다(https → wss). `VITE_WS_URL`은 그 규칙이 안
 * 맞는 자리(별도 도메인의 서버)를 위한 덮어쓰기다 - 빌드 시점에 굳는 값이라 `.env`에 두고, 없으면 아무 일도 없다.
 *
 * ## 토큰은 `sessionStorage`에 산다 - `localStorage`가 아니다
 *
 * 재접속 토큰은 **이 탭의 좌석**이다. 새로고침은 같은 탭이라 토큰이 살아 있어야 "자리로 돌아왔다"가 되고(규칙 2), 다른 탭은
 * 다른 좌석이어야 한다 - `localStorage`에 두면 두 탭이 같은 토큰으로 들어와 서로 `superseded`로 밀어내며 영원히 번갈아 끊긴다
 * (`wsTransport.ts` 머리말). 탭이 닫히면 토큰도 사라지는 것이 옳다 - 유예(60초)가 지나면 서버도 잊는다.
 *
 * 저장 형태는 방 코드 + 토큰 + 이름 한 덩어리다. 코드를 함께 두는 이유는 다른 방의 `?room=`으로 들어갈 때 옛 토큰을 보내지 않기
 * 위해서다(서버는 모르는 토큰을 새 사람으로 치지만, 보내지 않는 쪽이 뜻이 분명하다). 이름은 로비 입력의 기본값으로 다시 쓰인다.
 *
 * `sessionStorage`는 있는 척하면서 던지는 환경이 있다(사파리 프라이빗 · 쿠키 차단). 토큰 하나 못 읽었다고 방에 못 들어가면 안
 * 되므로 읽기·쓰기 모두 조용히 실패한다(`i18n/index.ts`의 `localStorage` 규약과 같다). 이 파일이 저장소를 만지는 유일한 자리라는
 * 것은 `netBoot.test.ts`가 소스에서 검사한다.
 */

import type { RoomVisibility } from '../net/protocol'
import { createRoomBrowse, type RoomBrowse, type RoomBrowseOptions } from '../net/roomBrowse'
import { createWsTransport, type HelloRequest, type WsTransport } from '../net/wsTransport'

/* --- 상수 ------------------------------------------------------------------------ */

/** 같은 호스트에서 소켓이 열리는 경로. nginx(`deploy/nginx/wierd-room.conf`)와 Vite 프록시(`vite.config.ts`)가 같은 값을 안다. */
export const DEFAULT_WS_PATH = '/ws'

/** 저장 키. 다른 저장값과 섞이지 않게 게임 이름을 앞에 둔다(`i18n`의 `weird-room.locale`과 같은 규약). */
const SEAT_STORAGE_KEY = 'weird-room.seat'
const NAME_STORAGE_KEY = 'weird-room.name'

/* --- 타입 ------------------------------------------------------------------------ */

/** 이 탭이 마지막으로 앉았던 좌석. 방 코드가 다르면 쓰지 않는다(머리말). */
export type SeatMemory = Readonly<{ roomCode: string; token: string; name: string }>

/** `sessionStorage`의 표면. 테스트가 가짜를 넣는다. */
export type StorageLike = Readonly<{
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}>

export type NetBootOptions = Readonly<{
  env?: Readonly<{ VITE_WS_URL?: string | undefined }> | undefined
  location?: Readonly<{ protocol: string; host: string }> | undefined
  storage?: StorageLike | null | undefined
}>

export type NetBoot = Readonly<{
  /** 소켓이 열릴 주소. */
  url: string
  /**
   * 소켓 전송 하나. 저장된 좌석이 그 방의 것이면 토큰을 실어 보내고, `welcome`이 준 토큰을 저장한다.
   * `visibility`는 **새 방을 만들 때만** 뜻이 있다(M14) - 없으면 서버가 비공개로 연다.
   */
  connect: (hello: Readonly<{ roomCode: string; name: string; visibility?: RoomVisibility }>) => WsTransport
  /** 방 목록 소켓(M14). 좌석이 없으므로 토큰도 저장도 지나지 않는다 - 이 파일이 아는 것은 주소 하나다. */
  browse: (handlers: Omit<RoomBrowseOptions, 'url'>) => RoomBrowse
  /** 그 방에 앉았던 기억. 없거나 다른 방이면 `null`. */
  rememberedSeat: (roomCode: string) => SeatMemory | null
  /** 마지막으로 적은 이름. 로비 입력의 기본값이다. */
  rememberedName: () => string | null
  rememberName: (name: string) => void
  /** 좌석을 잊는다 - 방을 스스로 나갔을 때. 새로고침이 그 방으로 되돌아가면 안 된다. */
  forgetSeat: () => void
}>

/* --- 주소 ------------------------------------------------------------------------ */

/**
 * 소켓 주소. `VITE_WS_URL`이 있으면 그대로, 없으면 같은 호스트의 `/ws`.
 * 프로토콜은 페이지를 따른다 - https 페이지에서 `ws://`를 열면 브라우저가 혼합 콘텐츠로 막는다.
 */
export function resolveWsUrl(
  env: Readonly<{ VITE_WS_URL?: string | undefined }>,
  location: Readonly<{ protocol: string; host: string }>,
): string {
  const override = env.VITE_WS_URL?.trim()
  if (override !== undefined && override !== '') return override
  const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${scheme}//${location.host}${DEFAULT_WS_PATH}`
}

/* --- 저장소 ------------------------------------------------------------------------ */

function browserStorage(): StorageLike | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

function readSeat(storage: StorageLike | null): SeatMemory | null {
  try {
    const raw = storage?.getItem(SEAT_STORAGE_KEY)
    if (raw === null || raw === undefined) return null
    const value: unknown = JSON.parse(raw)
    if (typeof value !== 'object' || value === null) return null
    const seat = value as Record<string, unknown>
    if (typeof seat['roomCode'] !== 'string' || typeof seat['token'] !== 'string' || typeof seat['name'] !== 'string') return null
    return { roomCode: seat['roomCode'], token: seat['token'], name: seat['name'] }
  } catch {
    return null
  }
}

function writeSeat(storage: StorageLike | null, seat: SeatMemory | null): void {
  try {
    if (seat === null) storage?.removeItem(SEAT_STORAGE_KEY)
    else storage?.setItem(SEAT_STORAGE_KEY, JSON.stringify(seat))
  } catch {
    // 저장하지 못해도 이번 접속은 살아 있다. 새로고침이 새 사람으로 앉을 뿐이다.
  }
}

function readName(storage: StorageLike | null): string | null {
  try {
    const raw = storage?.getItem(NAME_STORAGE_KEY)
    return raw === null || raw === undefined || raw.trim() === '' ? null : raw
  } catch {
    return null
  }
}

function writeName(storage: StorageLike | null, name: string): void {
  try {
    storage?.setItem(NAME_STORAGE_KEY, name)
  } catch {
    // 이름 하나다. 다음에 다시 적으면 된다.
  }
}

/* --- 입구 ------------------------------------------------------------------------ */

export function createNetBoot(options: NetBootOptions = {}): NetBoot {
  const storage = options.storage === undefined ? browserStorage() : options.storage
  const env = options.env ?? import.meta.env
  const location = options.location ?? window.location
  const url = resolveWsUrl(env, location)

  function rememberedSeat(roomCode: string): SeatMemory | null {
    const seat = readSeat(storage)
    return seat !== null && seat.roomCode === roomCode ? seat : null
  }

  return {
    url,

    connect(hello): WsTransport {
      const seat = rememberedSeat(hello.roomCode)
      const request: HelloRequest = {
        roomCode: hello.roomCode,
        name: hello.name,
        ...(seat === null ? {} : { token: seat.token }),
        ...(hello.visibility === undefined ? {} : { visibility: hello.visibility }),
      }
      writeName(storage, hello.name)
      return createWsTransport({
        url,
        hello: request,
        // `welcome`마다 갈아 쓴다 - 방을 만든 경우 코드는 여기서 처음 알게 된다.
        onToken: (token, roomCode) => writeSeat(storage, { roomCode, token, name: hello.name }),
      })
    },

    browse: (handlers) => createRoomBrowse({ url, ...handlers }),

    rememberedSeat,
    rememberedName: () => readName(storage),
    rememberName: (name) => writeName(storage, name),
    forgetSeat: () => writeSeat(storage, null),
  }
}
